import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, filter, catchError, tap } from 'rxjs/operators';
import { ApprovalState, ApprovalUserRole, Client, IApprovalFlowDto, IGeneralTabDto, ReportType } from '../api/api-clientInformation';
import { CurrentUserService } from './current-user.service';
import { ApprovalFlowService, ReportFormPage, ReportFormStore } from '../reports/services';
import { AuthService, Role } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class CommonUserService {
  private apiClient = inject(Client);
  private _store = inject(ReportFormStore);
  private _auth = inject(AuthService);
  private _userService = inject(CurrentUserService);

  approvalFlowState = toSignal(this._store.approvalFlow$);

  private userEmails = new BehaviorSubject<string[]>([]);
  public userEmails$ = this.userEmails.asObservable();

  // private _mitigationTab = toSignal(this._store.mitigationResolution$);
  public isAIOwner = signal<boolean>(false);

  constructor() {
    this.loadCurrentUserEmails().subscribe();
  }

  private normalizeEmail(email: string | null | undefined): string {
    return (email ?? '').trim().toLowerCase();
  }

  private getCurrentUserEmails(): string[] {
    const emails = this.userEmails.value
      .map(email => this.normalizeEmail(email))
      .filter(Boolean);

    const tokenEmail = this.normalizeEmail(this._userService.email());
    if (tokenEmail) {
      emails.push(tokenEmail);
    }

    return [...new Set(emails)];
  }
    private loadCurrentUserEmails(): Observable<string[]> {
      return toObservable(this._userService.email).pipe(
        filter((email): email is string => !!email),
        switchMap(email => {
          // console.log('email', email);
          
          return this.apiClient.peopleBriefData(email).pipe(
            catchError(error => {
              console.error('Error fetching people brief data:', error);
              return of(null);
            })
          )
        }),
        map(peopleData => {
              const tokenEmail = this.normalizeEmail(this._userService.email());

              if (!peopleData) {
                return tokenEmail ? [tokenEmail] : [];
              }

              const emails = Object.values(peopleData)
                .filter((value): value is string => typeof value === 'string' && value.length > 0)
                .map(email => this.normalizeEmail(email));

              if (tokenEmail) {
                emails.push(tokenEmail);
              }

          return [...new Set(emails)];
        }),
        tap(emails => {
          this.userEmails.next(emails);
        })
      );
    }


  public isEmailValid(email: string): Observable<boolean> {
    return this.userEmails$.pipe(
      map(() => this.getCurrentUserEmails().includes(this.normalizeEmail(email)))
    );
  }


  public matchesWithField(email: string): boolean {
    const emails = this.getCurrentUserEmails();
    return emails.includes(this.normalizeEmail(email));
  }


  public getCurrentUserApprovalRole(approvalFlowData?: any,general?: any,): ApprovalUserRole | null {
    // const approvalFlowData = this._approvalFlowSerive.tab();
    const userEmails = this.getCurrentUserEmails();


    if (!approvalFlowData || userEmails.length === 0) {
      return null;
    }

    const fieldRoleMapping = [
      { field: general?.initiator, role: ApprovalUserRole.Initiator },
      { field: general?.owner, role: ApprovalUserRole.Owner },
      { field: approvalFlowData.areaLeadInspEng, role: ApprovalUserRole.AreaLeadInspEng },
      { field: approvalFlowData.inspEngSupervisor, role: ApprovalUserRole.InspEngSupervisor },
      { field: approvalFlowData.srOpsConsultant, role: ApprovalUserRole.OpsAdvisor },
      { field: approvalFlowData.localTechnicalAuthority, role: ApprovalUserRole.LocalTechnicalAuthority },
      { field: approvalFlowData.assetTypeHCEPO, role: ApprovalUserRole.AssetTypeHCEPO },
      { field: approvalFlowData.areaOpsSuperintendent, role: ApprovalUserRole.AreaOpsSuperintendent },
      { field: approvalFlowData.productionOperationManager, role: ApprovalUserRole.ProductionOperationManager },
      { field: approvalFlowData.generalManagerOperation, role: ApprovalUserRole.GeneralManagerOperation },
      { field: approvalFlowData.generalDirectorOperation, role: ApprovalUserRole.GeneralDirector },
    ];

    for (const mapping of fieldRoleMapping) {
      if (mapping.field && this.isEmailMatchingUser(mapping.field)) {
        return mapping.role;
      }
    }

    return null;
  }
  //testing works
  public getAllMatchingUserApprovalRoles(approvalFlowData?: any, general?: any): ApprovalUserRole[] {
  const userEmails = this.getCurrentUserEmails();

  if (!approvalFlowData || userEmails.length === 0) {
    return [];
  }

  const fieldRoleMapping = [
    { field: general?.initiator, role: ApprovalUserRole.Initiator },
    { field: general?.owner, role: ApprovalUserRole.Owner },
    { field: approvalFlowData.areaLeadInspEng, role: ApprovalUserRole.AreaLeadInspEng },
    { field: approvalFlowData.inspEngSupervisor, role: ApprovalUserRole.InspEngSupervisor },
    { field: approvalFlowData.srOpsConsultant, role: ApprovalUserRole.OpsAdvisor },
    { field: approvalFlowData.localTechnicalAuthority, role: ApprovalUserRole.LocalTechnicalAuthority },
    { field: approvalFlowData.assetTypeHCEPO, role: ApprovalUserRole.AssetTypeHCEPO },
    { field: approvalFlowData.areaOpsSuperintendent, role: ApprovalUserRole.AreaOpsSuperintendent },
    { field: approvalFlowData.productionOperationManager, role: ApprovalUserRole.ProductionOperationManager },
    { field: approvalFlowData.generalManagerOperation, role: ApprovalUserRole.GeneralManagerOperation },
    { field: approvalFlowData.generalDirectorOperation, role: ApprovalUserRole.GeneralDirector },
  ];

  const matchingRoles: ApprovalUserRole[] = [];

  for (const mapping of fieldRoleMapping) {
    if (mapping.field && this.isEmailMatchingUser(mapping.field)) {
      matchingRoles.push(mapping.role);
    }
  }

  return matchingRoles;
}



endorsers = computed(() => {
    const authorizedUserEmail = this._userService.email();
    const approvalState = this.approvalFlowState();

    const endorsers = [
      {
        email: approvalState?.additionalEndorser1,
        endorsed: approvalState?.additionalEndorser1Endorsed,
      }, 
      {
        email: approvalState?.additionalEndorser2,
        endorsed: approvalState?.additionalEndorser2Endorsed,
      }, 
      {
        email: approvalState?.additionalEndorser3,
        endorsed: approvalState?.additionalEndorser3Endorsed,
      }, 
    ];

    const haveEndorsers = endorsers.some(item => item.email != undefined);
    const activeEndorsers = endorsers.filter(item => item.email != undefined);
    const allApproved = activeEndorsers.every(item => item.endorsed);
    const isEndorser1Active = !endorsers[0].endorsed && authorizedUserEmail === endorsers[0].email;
    // const isEndorser2Active = endorsers[0].endorsed && !endorsers[1].endorsed && authorizedUserEmail === endorsers[1].email;
    const isEndorser2Active = true
    const isEndorser3Active = endorsers[1].endorsed && !endorsers[2].endorsed && authorizedUserEmail === endorsers[2].email;
    const isEndorserActive = haveEndorsers && (isEndorser1Active || isEndorser2Active || isEndorser3Active);

    return { haveEndorsers, allApproved, isEndorserActive, isEndorser1Active, isEndorser2Active, isEndorser3Active };
  });

  public getCurrentUserApprovalRoleV2(approvalFlowData: IApprovalFlowDto | null, general: IGeneralTabDto, tab: ReportFormPage): ApprovalUserRole | null {
    const userEmails = this.getCurrentUserEmails();
    const state = general?.currentState;
    const reportType = general?.reportType;
    const auth_level = this._store.assignedManagementAuthorizationLevel();
    const criticality = this._store.criticality();
    const endorsers = this.endorsers();
    const isHCE = criticality && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(criticality);

    if (
      // !approvalFlowData ||
      userEmails.length === 0
    ) {
      return null;
    }

    // Keep initiator permissions stable after reopening a Draft report,
    // even when user also has RIPTRisk and FastRepi fallback would match.
    if (state === ApprovalState.Draft && general?.initiator && this.isEmailMatchingUser(general.initiator)) {
      return ApprovalUserRole.Initiator;
    }

    const fieldRoleMapping = [
      {
        field: null,
        role: ApprovalUserRole.ActionItemOwner,
        rules: [
          this.isAIOwner()
        ]
      },
      {
        field: approvalFlowData?.areaLeadInspEng,
        role: ApprovalUserRole.AreaLeadInspEng,
        // hasApproved: !approvalFlowData.hasAreaLeadApproved,
        rules: [
          state === ApprovalState.DraftedForReview && approvalFlowData?.areaLeadInspEng != undefined && !approvalFlowData.hasAreaLeadApproved,
          state === ApprovalState.PlanConfirmed && reportType === ReportType.AnomalyReport && approvalFlowData?.hasAreaLeadApproved,
        ],
        // state: [ApprovalState.DraftedForReview, ApprovalState.PlanConfirmed]
      },
      {
        field: approvalFlowData?.inspEngSupervisor,
        role: ApprovalUserRole.InspEngSupervisor,
        rules: [
          state === ApprovalState.DraftedForReview && approvalFlowData?.hasAreaLeadApproved,
          state === ApprovalState.PlanConfirmed && reportType === ReportType.AnomalyReport && approvalFlowData?.inspEngSupervisor != undefined,
        ],
      },
      {
        field: approvalFlowData?.srOpsConsultant,
        role: ApprovalUserRole.OpsAdvisor,
        // hasApproved: false,
        rules: [
          state === ApprovalState.SubmittedToOpsAdvisor,
          state === ApprovalState.RiskEvaluationScored, // && reportType && [ReportType.TaskDeferral, ReportType.IntegrityDeficiency].includes(reportType),
          // AI
          // tab === ReportFormPage.ACTION_ITEM && ReportType.AnomalyReport === reportType && state && [ApprovalState.Draft, ApprovalState.DraftedForReview, ApprovalState.PlanApproved, ApprovalState.PlanConfirmed, ApprovalState.PlanEndorsed, ApprovalState.RIPTScoredSkipped].includes(state),
          tab === ReportFormPage.ACTION_ITEM && ReportType.IntegrityDeficiency === reportType && state && [ApprovalState.Draft, ApprovalState.DraftedForReview, ApprovalState.PlanApproved, ApprovalState.PlanConfirmed, ApprovalState.PlanEndorsed, ApprovalState.RIPTScored, ApprovalState.RiskEvaluationScored, ApprovalState.SubmittedToOpsAdvisor].includes(state),
          tab === ReportFormPage.ACTION_ITEM && ReportType.ReliabilityDeficiency === reportType && state && [ApprovalState.Draft, ApprovalState.DraftedForReview, ApprovalState.PlanApproved, ApprovalState.PlanConfirmed, ApprovalState.PlanEndorsed, ApprovalState.RIPTScored, ApprovalState.RiskEvaluationScored, ApprovalState.SubmittedToOpsAdvisor].includes(state),
          tab === ReportFormPage.ACTION_ITEM && ReportType.IntegrityThreat === reportType && state && [ApprovalState.Draft, ApprovalState.DraftedForReview, ApprovalState.PlanApproved, ApprovalState.PlanConfirmed, ApprovalState.PlanEndorsed, ApprovalState.RIPTScored, ApprovalState.SubmittedToOpsAdvisor].includes(state),
          tab === ReportFormPage.ACTION_ITEM && ReportType.ReliabilityThreat === reportType && state && [ApprovalState.Draft, ApprovalState.DraftedForReview, ApprovalState.PlanApproved, ApprovalState.PlanConfirmed, ApprovalState.PlanEndorsed, ApprovalState.RIPTScored, ApprovalState.SubmittedToOpsAdvisor].includes(state),
          tab === ReportFormPage.ACTION_ITEM && ReportType.TaskDeferral === reportType && state && [ApprovalState.Draft, ApprovalState.DraftedForReview, ApprovalState.PlanApproved, ApprovalState.PlanConfirmed, ApprovalState.PlanEndorsed, ApprovalState.RiskEvaluationScored, ApprovalState.SubmittedToOpsAdvisor].includes(state),
        ],
      },
      {
        field: approvalFlowData?.localTechnicalAuthority,
        role: ApprovalUserRole.LocalTechnicalAuthority,
        // hasApproved: approvalFlowData?.hasLTAApproved,
        rules: [
          state === ApprovalState.RIPTScored && reportType && [ReportType.IntegrityDeficiency].includes(reportType) && criticality && ['IC1', 'IC2'].includes(criticality),
          state === ApprovalState.RIPTScored && reportType && [ReportType.ReliabilityDeficiency].includes(reportType) && criticality && ['AC1', 'AC2'].includes(criticality),
        ],
      },
      {
        field: approvalFlowData?.assetTypeHCEPO,
        role: ApprovalUserRole.AssetTypeHCEPO,
        // hasApproved: approvalFlowData?.hasHCEPOApproved,
        rules: [
          state === ApprovalState.PlanConfirmed && reportType && [ReportType.TaskDeferral].includes(reportType) && (!endorsers.haveEndorsers || endorsers.allApproved),
          state === ApprovalState.RIPTScored && reportType && [ReportType.IntegrityThreat, ReportType.ReliabilityThreat].includes(reportType) && isHCE && auth_level === 'A' && approvalFlowData?.assetTypeHCEPO != undefined && (!endorsers.haveEndorsers || endorsers.allApproved),
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.IntegrityDeficiency].includes(reportType) && criticality && ['IC1', 'IC2'].includes(criticality) && approvalFlowData?.hasLTAApproved && !approvalFlowData?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved),
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.ReliabilityDeficiency].includes(reportType) && criticality && ['AC1', 'AC2'].includes(criticality) && approvalFlowData?.hasLTAApproved && !approvalFlowData?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved),
        ],
      },
      
      // define Additional Endorserd (#1, #2, #3)
      {
        field: approvalFlowData?.additionalEndorser1,
        role: ApprovalUserRole.AdditionalEndorser1,
        rules: [
          reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && 
          (state === ApprovalState.PlanEndorsed && approvalFlowData?.hasLTAApproved || state === ApprovalState.RIPTScored && criticality === 'IC3') && 
          !approvalFlowData?.hasHCEPOApproved &&
          endorsers.isEndorser1Active,
        ],
      },
      
      {
        field: approvalFlowData?.additionalEndorser2,
        role: ApprovalUserRole.AdditionalEndorser2,
        rules: [
          reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && 
          (state === ApprovalState.PlanEndorsed && approvalFlowData?.hasLTAApproved || state === ApprovalState.RIPTScored && criticality === 'IC3') && 
          !approvalFlowData?.hasHCEPOApproved &&
          endorsers.isEndorser2Active,
        ],
      },

      {
        field: approvalFlowData?.additionalEndorser3,
        role: ApprovalUserRole.AdditionalEndorser3,
        rules: [
          reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && 
          (state === ApprovalState.PlanEndorsed && approvalFlowData?.hasLTAApproved || state === ApprovalState.RIPTScored && criticality === 'IC3') && 
          !approvalFlowData?.hasHCEPOApproved &&
          endorsers.isEndorser3Active,
        ],
      },

      {
        field: approvalFlowData?.areaOpsSuperintendent,
        role: ApprovalUserRole.AreaOpsSuperintendent,
        // hasApproved: approvalFlowData?.hasAreaOpsApproved,
        rules: [
          state === ApprovalState.PlanConfirmed && reportType && [ReportType.TaskDeferral].includes(reportType) && !isHCE,
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.TaskDeferral].includes(reportType) && isHCE && auth_level !== 'A' && approvalFlowData?.hasHCEPOApproved && !approvalFlowData?.hasAreaOpsApproved,
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.IntegrityThreat, ReportType.ReliabilityThreat].includes(reportType) && isHCE && auth_level === 'A' && approvalFlowData?.hasHCEPOApproved && !approvalFlowData?.hasAreaOpsApproved,
          state === ApprovalState.RIPTScored && reportType && [ReportType.IntegrityThreat, ReportType.ReliabilityThreat].includes(reportType) && !isHCE && auth_level !== 'A' && !approvalFlowData?.hasAreaOpsApproved,
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && isHCE && approvalFlowData?.hasHCEPOApproved && !approvalFlowData?.hasAreaOpsApproved,
          state === ApprovalState.RIPTScored && reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && !isHCE,
        ],
      },
      {
        field: approvalFlowData?.productionOperationManager,
        role: ApprovalUserRole.ProductionOperationManager,
        // hasApproved: approvalFlowData?.hasProdOpManagerApproved,
        rules: [
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.TaskDeferral].includes(reportType) && isHCE && auth_level !== 'A' && approvalFlowData?.hasAreaOpsApproved && !approvalFlowData?.hasProdOpManagerApproved,
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && isHCE && approvalFlowData?.hasAreaOpsApproved && !approvalFlowData?.hasProdOpManagerApproved,
        ],
      },
      {
        field: approvalFlowData?.generalManagerOperation,
        role: ApprovalUserRole.GeneralManagerOperation,
        // hasApproved: approvalFlowData.hasGenManagerOperatorApproved,
        rules: [
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.TaskDeferral].includes(reportType) && isHCE && auth_level && !['A', 'B'].includes(auth_level) && approvalFlowData?.hasProdOpManagerApproved && !approvalFlowData?.hasGenManagerOperatorApproved,
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && isHCE && approvalFlowData?.hasProdOpManagerApproved && !approvalFlowData?.hasGenManagerOperatorApproved,
        ],
      },
      {
        field: approvalFlowData?.generalDirectorOperation,
        role: ApprovalUserRole.GeneralDirector,
        // hasApproved: approvalFlowData?.hasDirectorApproved,
        rules: [
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.TaskDeferral].includes(reportType) && isHCE && auth_level === 'D' && approvalFlowData?.hasGenManagerOperatorApproved && !approvalFlowData?.hasDirectorApproved,
          state === ApprovalState.PlanEndorsed && reportType && [ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType) && isHCE && approvalFlowData?.hasGenManagerOperatorApproved && !approvalFlowData?.hasDirectorApproved,
        ],
      },
      {
        field: general?.initiator,
        role: ApprovalUserRole.Initiator,
        // rules: [
        //   state === ApprovalState.Draft,
        //   state === ApprovalState.ResolutionPlanCompleted,
        // ]
      },
      {
        field: general?.owner,
        role: ApprovalUserRole.Owner,
        // rules: [
        //   state === ApprovalState.ValidationAndVerificationCompleted,
        // ]
      },
      {
        field: null,
        role: ApprovalUserRole.FastRepi,
        rules: [
          // TODO: check FastRepi role
          this._auth.isInRole(Role.RIPTRisk) //&& state === ApprovalState.PlanConfirmed && reportType && [ReportType.IntegrityThreat, ReportType.ReliabilityThreat, ReportType.IntegrityDeficiency, ReportType.ReliabilityDeficiency].includes(reportType),
        ]
      },
    ];

    // On the Action Item tab, the report Owner must be able to change the
    // Action Item Owner field while the report is still editable (until Plan
    // Approved), even when the same user also holds an earlier-priority
    // approval-flow role (e.g. OpsAdvisor). Resolve to Owner first in that case.
    const ownerEditableAIStates = [
      ApprovalState.Draft,
      ApprovalState.DraftedForReview,
      ApprovalState.SubmittedToOpsAdvisor,
      ApprovalState.RIPTScored,
      ApprovalState.RiskEvaluationScored,
      ApprovalState.PlanConfirmed,
      ApprovalState.PlanEndorsed,
    ];
    if (
      tab === ReportFormPage.ACTION_ITEM &&
      reportType !== ReportType.AnomalyReport &&
      general?.owner &&
      this.isEmailMatchingUser(general.owner) &&
      state != undefined &&
      ownerEditableAIStates.includes(state)
    ) {
      return ApprovalUserRole.Owner;
    }

    for (const mapping of fieldRoleMapping) {
      if (
        (mapping.rules == undefined || mapping.rules?.some(Boolean)) &&
        (
          mapping.field && this.isEmailMatchingUser(mapping.field) ||
          mapping.role === ApprovalUserRole.FastRepi ||
          mapping.role === ApprovalUserRole.ActionItemOwner
        )
      ) {
        return mapping.role;
      }
    }

    return null;
  }

  public isEmailMatchingUser(email: string): boolean {
    if (!email) {
      return false;
    }

    const userEmails = this.getCurrentUserEmails();
    const normalizedEmail = this.normalizeEmail(email);

    return userEmails.some(userEmail => userEmail === normalizedEmail);
  }

}
